import { ApplicationInsights, type IExceptionTelemetry, type ITelemetryItem, type ITraceTelemetry } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { createBrowserHistory } from 'history';

/**
 * HAT-1081: Application Insights לא נטען יותר בעליית האפליקציה אלא רק אחרי
 * שהמשתמש הסכים לכלי המדידה (start מהקשר ההסכמה). לפני כן כל קריאה היא no-op
 * שקט - הלוגים ממשיכים ל-console דרך Log.tsx. ה-authenticated user נשמר בצד
 * כדי שהתחברות שקרתה לפני מתן ההסכמה תוחל ברגע שהכלי עולה.
 */
class LazyAppInsightsService {
  private instance: ApplicationInsights | null = null;
  private authenticatedUserId: string | null = null;
  /** initializers שנרשמו לפני ההסכמה - מוחלים ברגע שה-SDK עולה. */
  private pendingInitializers: Array<(item: ITelemetryItem) => boolean | void> = [];

  /** נקרא רק אחרי הסכמה. אידמפוטנטי - קריאה חוזרת (למשל StrictMode) לא טוענת פעמיים. */
  start(): void {
    if (this.instance || !import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING) return;

    const reactPlugin = new ReactPlugin();
    const clickPluginInstance = new ClickAnalyticsPlugin();

    const instance = new ApplicationInsights({
      config: {
        connectionString: import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING,
        enableAutoRouteTracking: true,
        enableAjaxErrorStatusText: true,
        extensions: [reactPlugin, clickPluginInstance],
        extensionConfig: {
          [reactPlugin.identifier]: { history: createBrowserHistory() },
          [clickPluginInstance.identifier]: { autoCapture: true },
        },
      },
    });

    instance.loadAppInsights();
    this.instance = instance;

    if (this.authenticatedUserId) {
      instance.setAuthenticatedUserContext(this.authenticatedUserId);
    }
  }

  isStarted(): boolean {
    return this.instance !== null;
  }

  setAuthenticatedUserContext(authenticatedUserId: string): void {
    this.authenticatedUserId = authenticatedUserId;
    this.instance?.setAuthenticatedUserContext(authenticatedUserId);
  }

  clearAuthenticatedUserContext(): void {
    this.authenticatedUserId = null;
    this.instance?.clearAuthenticatedUserContext();
  }

  addTelemetryInitializer(initializer: (item: ITelemetryItem) => boolean | void): void {
    this.pendingInitializers.push(initializer);
    this.instance?.addTelemetryInitializer(initializer);
  }

  trackException(exception: IExceptionTelemetry): void {
    this.instance?.trackException(exception);
  }

  trackTrace(trace: ITraceTelemetry): void {
    this.instance?.trackTrace(trace);
  }
}

const appInsightsService = new LazyAppInsightsService();

/**
 * Chromium reports "ResizeObserver loop completed with undelivered notifications"
 * as a window error whenever an observer needs a second layout pass. It carries
 * no stack worth reading, and a single looping observer can bury the daily quota
 * in it - one did, 3.87M events in half a day. PostHog drops it too, in main.tsx.
 */
appInsightsService.addTelemetryInitializer((item) => {
  const message = String(item.baseData?.exceptions?.[0]?.message ?? item.baseData?.message ?? '');
  if (/ResizeObserver loop/i.test(message)) return false;
});

export default appInsightsService;
